import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Settings, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function MaintenanceAlertSettingsModal({ 
    open, 
    onOpenChange, 
    language,
    onSave
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        days_before: 7,
        km_before: 1000,
        enabled: true
    });

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await api.get('/maintenance-alerts/settings');
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchSettings();
        }
    }, [open]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/maintenance-alerts/settings', settings);
            toast.success(language === 'el' ? 'Οι ρυθμίσεις αποθηκεύτηκαν' : 'Settings saved');
            onSave?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md bg-white">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-slate-800">
                        <Settings className="w-5 h-5 text-slate-500" />
                        {language === 'el' ? 'Ρυθμίσεις Ειδοποιήσεων' : 'Alert Settings'}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-6 py-4">
                        {/* Enable/Disable */}
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-slate-700">
                                    {language === 'el' ? 'Ενεργοποίηση ειδοποιήσεων' : 'Enable notifications'}
                                </Label>
                                <p className="text-sm text-slate-500">
                                    {language === 'el' 
                                        ? 'Λήψη ειδοποιήσεων για επερχόμενες συντηρήσεις'
                                        : 'Receive alerts for upcoming maintenance'}
                                </p>
                            </div>
                            <Switch
                                checked={settings.enabled}
                                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                            />
                        </div>

                        {settings.enabled && (
                            <>
                                {/* Days before */}
                                <div className="space-y-2">
                                    <Label className="text-slate-700">
                                        {language === 'el' ? 'Ημέρες πριν την συντήρηση' : 'Days before maintenance'}
                                    </Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="1"
                                            max="90"
                                            value={settings.days_before}
                                            onChange={(e) => setSettings(prev => ({ 
                                                ...prev, 
                                                days_before: parseInt(e.target.value) || 7 
                                            }))}
                                            className="w-24"
                                        />
                                        <span className="text-slate-500">
                                            {language === 'el' ? 'ημέρες' : 'days'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        {language === 'el' 
                                            ? 'Θα ειδοποιηθείτε τόσες ημέρες πριν την προγραμματισμένη συντήρηση'
                                            : 'You will be notified this many days before scheduled maintenance'}
                                    </p>
                                </div>

                                {/* KM before */}
                                <div className="space-y-2">
                                    <Label className="text-slate-700">
                                        {language === 'el' ? 'Χιλιόμετρα πριν την συντήρηση' : 'Kilometers before maintenance'}
                                    </Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="100"
                                            max="10000"
                                            step="100"
                                            value={settings.km_before}
                                            onChange={(e) => setSettings(prev => ({ 
                                                ...prev, 
                                                km_before: parseInt(e.target.value) || 1000 
                                            }))}
                                            className="w-24"
                                        />
                                        <span className="text-slate-500">km</span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        {language === 'el' 
                                            ? 'Θα ειδοποιηθείτε όταν απομένουν τόσα χιλιόμετρα για την συντήρηση'
                                            : 'You will be notified when this many km remain until maintenance'}
                                    </p>
                                </div>

                                {/* Summary */}
                                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                    <p className="text-sm text-amber-800">
                                        <strong>{language === 'el' ? 'Σημείωση:' : 'Note:'}</strong>{' '}
                                        {language === 'el' 
                                            ? `Θα λαμβάνετε ειδοποίηση ${settings.days_before} ημέρες Ή ${settings.km_before.toLocaleString()} km πριν - όποιο έρθει πρώτο.`
                                            : `You will receive alerts ${settings.days_before} days OR ${settings.km_before.toLocaleString()} km before - whichever comes first.`}
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pt-4 border-t">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => onOpenChange(false)}
                                disabled={saving}
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'el' ? 'Αποθήκευση...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        {language === 'el' ? 'Αποθήκευση' : 'Save'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
